import { useEffect, useMemo, useRef } from "react";
import { Mountain, Plus, ShieldCheck, Sparkles, TrendingDown, TrendingUp, X } from "lucide-react";
import { MaterialSelect } from "@/components/height-map/MaterialSelect";
import {
  computeSlopeCoverage,
  renderSlopeMask,
} from "@/lib/heightMapUtils";

export const SlopeControls = ({
  sourceImageData,
  slopeAngles,
  layers,
  slopeSettings,
  onChange,
  ownerLayerIndices,
  compact = false,
  roundedMapPreview = false,
}) => {
  const maskCanvasRef = useRef(null);
  const selectedMaterial =
    layers.find((layer) => layer.id === slopeSettings.materialId) ??
    layers.find((layer) => layer.name.toLowerCase() === "rock") ??
    layers[0];
  const selectedMaterialId = selectedMaterial?.id ?? "";
  const protectedMaterialIds = useMemo(() => {
    const ids = Array.isArray(slopeSettings.protectedMaterialIds)
      ? slopeSettings.protectedMaterialIds
      : slopeSettings.protectedMaterialId
        ? [slopeSettings.protectedMaterialId]
        : [];
    return [...new Set(ids)].filter((id) => layers.some((layer) => layer.id === id));
  }, [layers, slopeSettings.protectedMaterialId, slopeSettings.protectedMaterialIds]);
  const protectedMaterials = useMemo(
    () => protectedMaterialIds
      .map((id) => layers.find((layer) => layer.id === id))
      .filter(Boolean),
    [layers, protectedMaterialIds],
  );
  const protectedOwnerLayerIndices = useMemo(
    () => protectedMaterials
      .map((material) => layers.findIndex((layer) => layer.id === material.id))
      .filter((index) => index >= 0),
    [layers, protectedMaterials],
  );
  const angle = Math.max(0, Math.min(90, slopeSettings.angle ?? 35));
  const comparison = slopeSettings.comparison === "lte" ? "lte" : "gte";
  const coverage = useMemo(
    () => computeSlopeCoverage(slopeAngles, angle, comparison, {
      sourceImageData,
      ownerLayerIndices,
      protectedOwnerLayerIndices,
    }),
    [angle, comparison, ownerLayerIndices, protectedOwnerLayerIndices, slopeAngles, sourceImageData],
  );

  useEffect(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas || !sourceImageData || !slopeAngles || !selectedMaterial) return;
    const mask = renderSlopeMask(sourceImageData, slopeAngles, {
      angle,
      comparison,
      color: selectedMaterial.color,
      ownerLayerIndices,
      protectedOwnerLayerIndices,
    });
    if (!mask) return;
    canvas.width = mask.width;
    canvas.height = mask.height;
    canvas.getContext("2d").putImageData(mask, 0, 0);
  }, [angle, comparison, ownerLayerIndices, protectedOwnerLayerIndices, selectedMaterial, slopeAngles, sourceImageData]);

  const radians = (angle * Math.PI) / 180;
  const lineLength = 100;
  const endX = 22 + Math.cos(radians) * lineLength;
  const endY = 76 - Math.sin(radians) * 56;
  const update = (patch) => onChange({ ...slopeSettings, ...patch });
  const updateProtectedMaterials = (protectedMaterialIds) => {
    const nextSettings = { ...slopeSettings, protectedMaterialIds };
    delete nextSettings.protectedMaterialId;
    onChange(nextSettings);
  };
  const addProtectedMaterial = () => {
    const nextMaterial = layers.find((layer) => !protectedMaterialIds.includes(layer.id));
    if (nextMaterial) updateProtectedMaterials([...protectedMaterialIds, nextMaterial.id]);
  };
  const replaceProtectedMaterial = (index, materialId) => {
    const nextIds = protectedMaterialIds.slice();
    nextIds[index] = materialId;
    updateProtectedMaterials([...new Set(nextIds)]);
  };
  const removeProtectedMaterial = (index) => {
    updateProtectedMaterials(protectedMaterialIds.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <section
      data-testid="slope-controls"
      className={`rounded-xl border border-zinc-800/80 bg-[#0d0e12] ${compact ? "p-3" : "p-5"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-rose-400/20 bg-rose-500/10 text-rose-300">
            <Mountain className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-zinc-100">Steep slope material</h2>
            <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-500">
              Apply one material to cliffs, steep terrain, or flatter areas using the estimated slope angle.
            </p>
          </div>
        </div>
        <span
          data-testid="slope-coverage"
          className="shrink-0 rounded-full border border-rose-400/20 bg-rose-500/10 px-2 py-1 text-[10px] font-semibold text-rose-300"
        >
          {sourceImageData ? `${coverage.toFixed(1)}% selected` : "Upload a map"}
        </span>
      </div>

      <div
        data-testid="slope-final-step-note"
        className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/20 bg-amber-500/5 px-3 py-2 text-[10px] leading-relaxed text-amber-100/80"
      >
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
        <span>
          <strong className="text-amber-200">Recommended final step:</strong> finish Elevation, Manual Paint, and Material Mix first, then apply Steep Slopes.
        </span>
      </div>

      <div className={`mt-4 grid gap-4 ${compact ? "grid-cols-1" : "lg:grid-cols-[minmax(260px,0.9fr)_minmax(320px,1.1fr)]"}`}>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              Material to apply
            </label>
            <MaterialSelect
              layers={layers}
              activeId={selectedMaterialId}
              onSelect={(materialId) => update({ materialId })}
              testId="slope-material"
            />
          </div>

          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              <ShieldCheck className="h-3.5 w-3.5 text-sky-300" />
              Protected materials
            </label>
            <div data-testid="slope-protected-materials" className="space-y-2">
              {protectedMaterials.length === 0 ? (
                <div className="rounded-lg border border-dashed border-zinc-800 bg-[#101116] px-3 py-2 text-[10px] text-zinc-600">
                  No protected materials. Every layer can be affected.
                </div>
              ) : (
                protectedMaterials.map((material, index) => {
                  const selectableLayers = layers.filter(
                    (layer) => layer.id === material.id || !protectedMaterialIds.includes(layer.id),
                  );
                  return (
                    <div key={material.id} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <MaterialSelect
                          layers={selectableLayers}
                          activeId={material.id}
                          onSelect={(materialId) => replaceProtectedMaterial(index, materialId)}
                          testId={`slope-protected-material-${index}`}
                        />
                      </div>
                      <button
                        type="button"
                        data-testid={`slope-protected-remove-${index}`}
                        onClick={() => removeProtectedMaterial(index)}
                        title={`Stop protecting ${material.name}`}
                        aria-label={`Remove ${material.name} from protected materials`}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-800 bg-[#101116] text-zinc-500 hover:border-rose-400/40 hover:text-rose-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
              <button
                type="button"
                data-testid="slope-add-protected-material"
                onClick={addProtectedMaterial}
                disabled={protectedMaterialIds.length >= layers.length}
                className="accent-border-hover flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-zinc-700 bg-[#101116] px-3 py-2 text-[10px] font-semibold text-zinc-400 hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
                Add protected material
              </button>
            </div>
            <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-600">
              Pixels already assigned to protected layers stay unchanged, even when they match the slope rule.
            </p>
          </div>

          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              Generate on
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-zinc-800/80 bg-[#101116] p-1">
              <RuleButton
                testId="slope-rule-gte"
                active={comparison === "gte"}
                onClick={() => update({ comparison: "gte" })}
                icon={<TrendingUp className="h-3.5 w-3.5" />}
                label="At or above"
                detail="Steeper"
              />
              <RuleButton
                testId="slope-rule-lte"
                active={comparison === "lte"}
                onClick={() => update({ comparison: "lte" })}
                icon={<TrendingDown className="h-3.5 w-3.5" />}
                label="At or below"
                detail="Flatter"
              />
            </div>
          </div>

          <div className="rounded-xl border border-zinc-800/80 bg-[#101116] p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-zinc-200">
                  {comparison === "gte" ? "Minimum slope" : "Maximum slope"}
                </div>
                <div className="text-[10px] text-zinc-500">
                  {comparison === "gte" ? "Select this angle and anything steeper" : "Select this angle and anything flatter"}
                </div>
              </div>
              <span data-testid="slope-angle-value" className="mono text-lg font-semibold text-rose-300">
                {angle}&deg;
              </span>
            </div>
            <input
              data-testid="slope-angle-slider"
              data-slider-thumb="true"
              type="range"
              min={0}
              max={90}
              step={1}
              value={angle}
              onChange={(event) => update({ angle: Number(event.target.value) })}
              className="w-full cursor-pointer accent-rose-500"
              aria-label={comparison === "gte" ? "Minimum slope angle" : "Maximum slope angle"}
            />
            <div className="mt-1 flex justify-between text-[9px] text-zinc-600">
              <span>0&deg; flat</span>
              <span>45&deg;</span>
              <span>90&deg; vertical</span>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <div className="overflow-hidden rounded-xl border border-zinc-800/80 bg-[#101116]">
            <div className="flex items-center justify-between border-b border-zinc-800/70 px-3 py-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Angle preview</span>
              <span className="text-[10px] text-zinc-400">Live</span>
            </div>
            <svg
              data-testid="slope-angle-preview"
              viewBox="0 0 150 92"
              className="h-24 w-full"
              role="img"
              aria-label={`${angle} degree slope preview`}
            >
              <defs>
                <linearGradient id="slope-preview-fill" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ff3d68" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#ff6b3d" stopOpacity="0.12" />
                </linearGradient>
              </defs>
              <path d={`M22 76 L${endX} ${endY} L${endX} 76 Z`} fill="url(#slope-preview-fill)" />
              <line x1="14" y1="76" x2="136" y2="76" stroke="#3f3f46" strokeWidth="2" />
              <line x1="22" y1="76" x2={endX} y2={endY} stroke="#fb7185" strokeWidth="4" strokeLinecap="round" />
              <circle cx={endX} cy={endY} r="4" fill="#fdba74" />
              <text x="22" y="88" fill="#71717a" fontSize="8">terrain slope</text>
              <text x="104" y="20" fill="#fda4af" fontSize="13" fontWeight="700">{angle}&deg;</text>
            </svg>
          </div>

          <div className="overflow-hidden rounded-xl border border-zinc-800/80 bg-[#08090b]">
            <div className="flex items-center justify-between border-b border-zinc-800/70 px-3 py-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Affected-area preview</span>
              <span className="flex items-center gap-2.5 text-[9px] text-zinc-500">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: selectedMaterial?.color }} />
                  selected
                </span>
                {protectedMaterials.length > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-slate-600" />
                    {protectedMaterials.length} protected
                  </span>
                )}
              </span>
            </div>
            <div className="flex h-32 items-center justify-center p-2 checker-bg">
              {sourceImageData ? (
                <canvas
                  data-testid="slope-mask-canvas"
                  ref={maskCanvasRef}
                  className={`max-h-full max-w-full object-contain ${roundedMapPreview ? "rounded-lg" : ""}`}
                  style={{ imageRendering: "pixelated" }}
                />
              ) : (
                <p className="px-3 text-center text-[11px] text-zinc-600">
                  Upload a height map to see the selected slopes update here.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const RuleButton = ({ active, onClick, icon, label, detail, testId }) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={active}
    onClick={onClick}
    className={`flex min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left transition ${
      active
        ? "brand-gradient text-white shadow-sm shadow-rose-500/20"
        : "text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100"
    }`}
  >
    {icon}
    <span className="min-w-0">
      <span className="block truncate text-[10px] font-semibold">{label}</span>
      <span className={`block text-[9px] ${active ? "text-white/75" : "text-zinc-600"}`}>{detail}</span>
    </span>
  </button>
);
